import { useCallback } from 'react';
import axiosInstance from '../../../axiosInstance';

/**
 * useRequestProgressApi Hook
 *
 * Pure API hook for request progress operations.
 * Handles switching progress (approve/reject/send-back) and fetching send-back targets.
 *
 * @returns {Object} - { switchProgress, getSendBackTargets }
 */
const useRequestProgressApi = () => {
  /**
   * Switches request progress to a new stage (approve, reject, send-back)
   * @param {Object} params - Switch parameters
   * @param {string} params.action - Action code (approved, rejected, sendback)
   * @param {number} params.actionStageId - Target stage ID
   * @param {number} params.requestsProgressId - Current progress ID
   * @param {number} params.wfVersionId - Workflow version ID
   * @param {string} [params.rejectionReason] - Optional rejection reason
   * @returns {Promise<Object>} - API response data
   */
  const switchProgress = useCallback(
    async ({
      action,
      actionStageId,
      requestsProgressId,
      wfVersionId,
      rejectionReason,
    }) => {
      const payload = {
        action,
        actionStageId,
        requestsProgressId,
        wf_version_id: wfVersionId,
      };

      if (rejectionReason) {
        payload.rejection_reason = rejectionReason;
      }

      const response = await axiosInstance.post(
        '/api/requests-progress/switch',
        payload
      );
      return response.data;
    },
    []
  );

  /**
   * Fetches eligible send-back targets for a request progress
   * @param {number} requestProgressId - Request progress ID
   * @returns {Promise<Array>} - Array of eligible send-back targets
   */
  const getSendBackTargets = useCallback(async (requestProgressId) => {
    const response = await axiosInstance.get(
      `/api/requests-progress/${requestProgressId}/send-back-targets`
    );
    return response.data?.targets || [];
  }, []);

  return { switchProgress, getSendBackTargets };
};

export default useRequestProgressApi;
